import React, { useState, useEffect } from 'react';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import { apiService } from 'services/base.service';
import { MetadataResponseType, MetadataType } from './types';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import useNewBrandContext from 'contexts/new-brand.context';
import useDashboardContext from 'contexts/dashboard.context';
import { CreateMetadataResponse, MetadataItemType } from 'pages/metadata/types';

const CREATE_ENDPOINT = {
  brand: 'keywords',
  meal: 'keywords',

  descriptive: 'marketing/create-brand-descriptive-metadata',
  business: 'marketing/create-brand-business-metadata'
};

const CreateBrandMetadata = () => {
  const { category } = useDashboardContext();
  const { createBrandForm, setCreateBrandForm } = useNewBrandContext();
  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [descriptiveMetadataList, setDescriptiveMetadataList] = useState<MetadataType[]>([]);
  const [businessMetadataList, setBusinessMetadataList] = useState<MetadataType[]>([]);

  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>([
    ...createBrandForm.brand_keywords
  ]);
  const [selectedDescriptiveMetadata, setSelectedDescriptiveMetadata] = useState<
    MetadataItemType[]
  >([...createBrandForm.brand_descriptive_metadatas]);
  const [selectedBusinessMetadata, setSelectedBusinessMetadata] = useState<MetadataItemType[]>([
    ...createBrandForm.brand_business_metadatas
  ]);

  const MAP_LOCAL_STATE = {
    brand: setKeywordsList,
    meal: setKeywordsList,

    descriptive: setDescriptiveMetadataList,
    business: setBusinessMetadataList
  };

  const getKeywordList = async () => {
    const res = await apiService.get('cokitchen-explore-keyword', {
      isInternal: true
    });
    const extractedMetadata: MetadataType[] = res.data.data.map((item: MetadataResponseType) => {
      return item.meal_keyword;
    });
    setDescriptiveMetadataList(extractedMetadata);
    setBusinessMetadataList(extractedMetadata);
  };

  const getBrandKeywordList = async () => {
    const res = await apiService.get('keywords', {
      params: { type: 'brand' },
      isInternal: true
    });
    const extractedMetadata: MetadataType[] = res.data.data;
    setKeywordsList(extractedMetadata);
  };

  useEffect(() => {
    getKeywordList();
    getBrandKeywordList();
  }, []);
  useEffect(() => {
    setCreateBrandForm({
      ...createBrandForm,
      brand_descriptive_metadatas: selectedDescriptiveMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_business_metadatas: selectedBusinessMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_keywords: selectedKeywords.map((keyword) => {
        return {
          name: keyword.name,
          id: keyword.id
        };
      })
    });
  }, [selectedKeywords, selectedDescriptiveMetadata, selectedBusinessMetadata]);

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([...selectedKeywords, item]);
    } else {
      setSelectedKeywords((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onDescriptiveTagClick = (item: MetadataItemType) => {
    const exists = !!selectedDescriptiveMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedDescriptiveMetadata([...selectedDescriptiveMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onBusinessTagClick = (item: MetadataItemType) => {
    const exists = !!selectedBusinessMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedBusinessMetadata([...selectedBusinessMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };

  const createMetadata = async (name: string, type: keyof typeof CREATE_ENDPOINT) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: {
          name,
          item_type: category?.toUpperCase()
        }
      });
      const { message, id } = res.data as CreateMetadataResponse;
      toast(<ToastMessage type='success' message={message} />);
      MAP_LOCAL_STATE[type]((p) => [{ name, id }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  const createKeyword = async (
    name: string,
    type: keyof typeof CREATE_ENDPOINT,
    popular: boolean
  ) => {
    try {
      const res = await apiService.post('keywords', {
        data: {
          name,
          type,
          popular
        }
      });
      const { message, id } = res.data as CreateMetadataResponse;
      toast(<ToastMessage type='success' message={message} />);
      setKeywordsList((p) => [{ name, id }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  return (
    <div>
      <MetadataDropdown
        title='Keywords'
        description='Select keyword'
        metadataList={keywordsList}
        selectedMetadata={createBrandForm.brand_keywords}
        onCreate={(w, popular) => createKeyword(w, 'brand', popular)}
        onTagClick={onKeywordTagClick}
      />
      <MetadataDropdown
        title='Descriptive Metadata'
        description='Select descriptive metadata'
        metadataList={descriptiveMetadataList}
        selectedMetadata={createBrandForm.brand_descriptive_metadatas}
        onCreate={(w) => createMetadata(w, 'descriptive')}
        onTagClick={onDescriptiveTagClick}
      />
      <MetadataDropdown
        title='Business Metadata'
        description='Select Business metadata'
        metadataList={businessMetadataList}
        selectedMetadata={createBrandForm.brand_business_metadatas}
        onCreate={(w) => createMetadata(w, 'business')}
        onTagClick={onBusinessTagClick}
      />
    </div>
  );
};

export default CreateBrandMetadata;
